import { useEffect, useRef } from "react";
import {
    createEngine,
    InspectControl,
    InspectManager,
    MapEngine,
    renderFeaturePopup,
    TileBoundaryManager,
    ToggleControl,
    XrayManager,
} from "../engine";
import {
    InspectMode,
    SideConfig,
    useViewerStore,
    usePanelStore,
    ViewerCamera,
} from "../state";
import { fetchStylesheetJson } from "./styleLoader";

const XRAY_ICON =
    '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
    'stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
    '<path d="M2 12h4l3 8 4-16 3 8h4"/></svg>';
const TILES_ICON =
    '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
    'stroke-width="2" stroke-linejoin="round">' +
    '<rect x="3" y="3" width="8" height="8"/><rect x="13" y="3" width="8" height="8"/>' +
    '<rect x="3" y="13" width="8" height="8"/><rect x="13" y="13" width="8" height="8"/></svg>';

/** Handles returned so callers (the ConfigBar Update action) can drive the map. */
export interface MapEngineHandle {
    /**
     * Applies a side config to the map: fetch style + tileset override, setStyle.
     * Resolves `true` when the style was actually applied, `false` when the fetch
     * failed (or the engine isn't mounted) so callers can skip marking the draft
     * applied on a no-op.
     */
    applyConfig: (config: SideConfig) => Promise<boolean>;
    /** The underlying engine, or null before mount. */
    engine: () => MapEngine | null;
}

/**
 * Owns the MapEngine lifecycle for a single map and wires it to the store:
 * creates the engine into `containerRef`, attaches managers (x-ray, tile
 * boundaries, inspect) and on-map controls, mirrors camera moves into the
 * store, and reflects instant toggles onto the map. Returns a handle the
 * ConfigBar uses to apply configs on Update. Single-view only (C4 slice).
 */
export function useMapEngine(
    containerRef: React.RefObject<HTMLDivElement>
): MapEngineHandle {
    const engineRef = useRef<MapEngine | null>(null);
    const xrayRef = useRef<XrayManager | null>(null);
    const tileRef = useRef<TileBoundaryManager | null>(null);
    const inspectRef = useRef<InspectManager | null>(null);

    // Pull the store actions/values we need imperatively.
    const setCamera = useViewerStore((s) => s.setCamera);

    useEffect(() => {
        if (!containerRef.current) return;

        const initialCamera = useViewerStore.getState().camera;
        const engine = createEngine({
            container: containerRef.current,
            style: null, // applied on first Update
            camera: initialCamera,
        });
        engineRef.current = engine;

        // Native controls, top-right (matching the 1.0 viewer's control stack).
        engine.addNavigationControl("top-right");
        engine.addFullscreenControl("top-right");

        // Managers.
        const xray = new XrayManager(engine);
        const tile = new TileBoundaryManager(engine);
        const inspect = new InspectManager(engine, {
            renderPopup: (lngLat, features) =>
                engine.showPopup(lngLat, renderFeaturePopup(features)),
            // Panel mode: push the clicked features into the panel store and open
            // the feature-inspect panel in the v2 panel host.
            onPanelResult: (result) =>
                usePanelStore.getState().openPanelById("feature-inspect", result),
        });
        inspect.attach();
        xrayRef.current = xray;
        tileRef.current = tile;
        inspectRef.current = inspect;

        // On-map toggle controls, top-right.
        const xrayControl = new ToggleControl({
            title: "X-Ray view",
            iconHtml: XRAY_ICON,
            onToggle: (on) => useViewerStore.getState().setXray(on),
        });
        const tileControl = new ToggleControl({
            title: "Tile boundaries",
            iconHtml: TILES_ICON,
            onToggle: (on) => useViewerStore.getState().setTileBoundaries(on),
        });
        const inspectControl = new InspectControl({
            onChange: (mode) => useViewerStore.getState().setInspect(mode as InspectMode),
        });
        engine.addControl(xrayControl, "top-right");
        engine.addControl(tileControl, "top-right");
        engine.addControl(inspectControl, "top-right");

        // Mirror camera moves into the store (throttle-free; moveend is coarse).
        const offMove = engine.on("moveend", () => {
            const c: ViewerCamera = engine.getCamera();
            setCamera(c);
        });

        return () => {
            offMove();
            inspect.dispose();
            engine.destroy();
            engineRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Reflect instant toggles from the store onto the managers.
    const toggles = useViewerStore((s) => s.toggles);
    useEffect(() => {
        xrayRef.current?.setActive(toggles.xray);
        tileRef.current?.setActive(toggles.tileBoundaries);
        inspectRef.current?.setMode(toggles.inspect);
    }, [toggles.xray, toggles.tileBoundaries, toggles.inspect]);

    return {
        engine: () => engineRef.current,
        applyConfig: async (config: SideConfig): Promise<boolean> => {
            const engine = engineRef.current;
            if (!engine) return false;
            const style = await fetchStylesheetJson(config);
            if (!style) return false;
            engine.setStyle(style);
            xrayRef.current?.setNormalStyle(style);
            return true;
        },
    };
}
